import { AstroMarkCompiler as AstroMarkPackageCompiler, type Diagnostic } from "@orbitalwatch/astromark";

import { AstroMarkCompiler as LegacyCompiler } from "./legacy/AstroMarkCompiler";
import type { AMTocNode } from "./legacy/AMTableOfContents";
import { adaptAstroMarkHtml, type AdaptedHeading } from "./adapter";
import { CONSTRUCT_EFFECT, detectLegacyConstructs } from "./dialect";
import { DEFAULT_PAGE_FORMAT, type PageFormat } from "./format";
import { sanitizePageHtml } from "../lib/infrastructure/sanitize/sanitizePageHtml";

/** One compiler finding, with the stable code from `@orbitalwatch/astromark` (`DiagnosticCode`) or Ark's `ARK_*` codes. */
export type RenderDiagnostic = Diagnostic;

/** A heading in the page's table of contents (same shape the reader's TOC has always taken). */
export type TocNode = AMTocNode;

export interface RenderPageInput {
    /** The page body as stored by Ark.Core. */
    body?: string | null;
    /** Which dialect to compile it as. Defaults to `DEFAULT_PAGE_FORMAT`; resolve it with `resolvePageFormat`. */
    format?: PageFormat;
}

export interface RenderPageResult {
    /** Sanitized HTML (always through `sanitizePageHtml`), ready for `dangerouslySetInnerHTML`. */
    html: string;
    /** Compiler findings. Always empty for `markdown-it`. */
    diagnostics: RenderDiagnostic[];
    /** The format that was used. */
    format: PageFormat;
}

/** Ark's own codes (warnings about markdown-it constructs on an AstroMark page). */
export const ArkDiagnosticCode = {
    LEGACY_SYNTAX: "ARK_LEGACY_SYNTAX",
} as const;

const astroMark = new AstroMarkPackageCompiler();

interface AstroMarkRender {
    html: string;
    headings: AdaptedHeading[];
    diagnostics: RenderDiagnostic[];
}

/** The last few AstroMark renders by body: the reader asks for the HTML and the TOC of the same body back to back. */
const cache = new Map<string, AstroMarkRender>();
const CACHE_SIZE = 8;

function renderAstroMark(body: string): AstroMarkRender {
    const cached = cache.get(body);
    if (cached) return cached;

    const compiled = astroMark.compileToHTMLWithDiagnostics(body);
    const adapted = adaptAstroMarkHtml(compiled.html);
    const legacy: RenderDiagnostic[] = detectLegacyConstructs(body)
        .filter((hit) => hit.construct !== "toc-placeholder")
        .map((hit) => ({
            severity: "warning",
            code: ArkDiagnosticCode.LEGACY_SYNTAX,
            message: `Line ${hit.line}${hit.count > 1 ? ` (+${hit.count - 1} more)` : ""}: ${CONSTRUCT_EFFECT[hit.construct]}.`,
            range: { start: { line: hit.line, column: 1 }, end: { line: hit.line, column: 1 } },
        }));

    const result: AstroMarkRender = {
        html: sanitizePageHtml(adapted.html),
        headings: adapted.headings,
        diagnostics: [...compiled.diagnostics, ...legacy],
    };
    cache.set(body, result);
    if (cache.size > CACHE_SIZE) cache.delete(cache.keys().next().value!);
    return result;
}

/**
 * A page body → sanitized HTML in the reader's class vocabulary, for either format. `markdown-it` is the legacy engine,
 * byte for byte what Ark rendered before (snapshot-tested); `astromark` is `@orbitalwatch/astromark` plus Ark's adapter
 * (`adapter.ts`). Both go through `sanitizePageHtml`.
 */
export function renderPage({ body, format = DEFAULT_PAGE_FORMAT }: RenderPageInput): RenderPageResult {
    if (format === "astromark") {
        const { html, diagnostics } = renderAstroMark(body ?? "");
        return { html, diagnostics, format };
    }
    return { html: LegacyCompiler.compileToHtml(body ?? undefined), diagnostics: [], format: "markdown-it" };
}

function tocFromHeadings(headings: AdaptedHeading[]): TocNode[] {
    const root: TocNode = { level: 0, label: "", anchor: "", children: [] };
    const stack: TocNode[] = [root];
    for (const heading of headings) {
        const node: TocNode = { level: heading.level, label: heading.label, anchor: `#${heading.id}`, children: [] };
        while (stack.length > 1 && node.level <= stack[stack.length - 1].level) stack.pop();
        stack[stack.length - 1].children.push(node);
        stack.push(node);
    }
    return root.children;
}

/** The page's headings as a nested TOC (what `AstroMarkCompiler.generateTocAst(body).children` returned). */
export function pageToc({ body, format = DEFAULT_PAGE_FORMAT }: RenderPageInput): TocNode[] {
    if (format === "astromark") return tocFromHeadings(renderAstroMark(body ?? "").headings);
    return LegacyCompiler.generateTocAst(body ?? undefined).children;
}
